/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { EChartsType } from 'echarts';
import { init } from 'echarts';
import { FC, memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { buildChartOptionFromTemplate } from './buildChartOptionFromTemplate';
import { parseTableCellChartData } from './parseTableCellChartData';

export const DEFAULT_TABLE_CELL_CHART_HEIGHT = 48;

export interface TableCellChartProps {
  template: string;
  cellValue: unknown;
  height?: number;
}

export const TableCellChart: FC<TableCellChartProps> = memo(
  ({ template, cellValue, height = DEFAULT_TABLE_CELL_CHART_HEIGHT }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<EChartsType | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const chart = init(el);
      chartRef.current = chart;
      return () => {
        chart.dispose();
        chartRef.current = null;
      };
    }, []);

    useEffect(() => {
      chartRef.current?.resize();
    }, [height]);

    useEffect(() => {
      const chart = chartRef.current;
      if (!chart) {
        return;
      }

      const parsed = parseTableCellChartData(cellValue);
      if (!parsed) {
        setError('Invalid chart data format');
        chart.clear();
        return;
      }

      try {
        const option = buildChartOptionFromTemplate(template, parsed);
        setError(null);
        chart.setOption(option, true);
        chart.resize();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Chart template error');
        chart.clear();
      }
    }, [template, cellValue]);

    if (error) {
      return <ErrorText>{error}</ErrorText>;
    }

    return <ChartContainer ref={containerRef} $height={height} />;
  },
);

const ChartContainer = styled.div<{ $height: number }>`
  min-width: 120px;
  height: ${p => p.$height}px;
  overflow: visible;
`;

const ErrorText = styled.span`
  color: #ff4d4f;
  font-size: 12px;
`;
